/**
 * Every image on the site, in one place.
 *
 * ---------------------------------------------------------------------------
 * IMPORTANT — the WordPress library contains three very different kinds of
 * asset, and their filenames are dangerously similar:
 *
 *   The-Local-Food-Menu1.webp            -> a PHOTOGRAPH of a dish
 *   The-Local-Food-Menu-0001-scaled.webp -> a SCAN of a printed menu page
 *
 * One has a hyphen and a four-digit number, the other doesn't. Using a menu
 * scan as a hero background or card image looks broken. They are kept in
 * separate exports below — never merge them.
 * ---------------------------------------------------------------------------
 *
 * HOW AN IMAGE URL IS RESOLVED
 *
 *   1. If the file has been downloaded into src/assets/media (by `npm run
 *      media`), use the webpack-bundled copy — content-hashed, cache-busted,
 *      and verified at build time. A missing file breaks the BUILD, loudly,
 *      instead of shipping a broken <img>.
 *   2. Otherwise fall back to the live WordPress URL.
 *
 * There is deliberately no "use local files" switch any more. An earlier
 * version had one, and setting it with a half-populated folder silently broke
 * every image on the site. Resolution is now per-file and self-checking:
 * whatever has been downloaded is used, whatever hasn't still loads remotely.
 */

import BUNDLED from '../assets/media/manifest';

const REMOTE_BASE = 'https://www.wethelocal.co/wp-content/uploads';

/** Resolve an upload path to a servable URL. Bundled copy wins; remote is the net. */
export function m(path) {
  if (!path) return '';
  const file = path.split('/').pop();
  return BUNDLED[file] || `${REMOTE_BASE}/${path}`;
}

/** How much of the library is bundled — surfaced by `npm run media:check`. */
export const bundledCount = () => Object.keys(BUNDLED).length;

/* ==========================================================================
   1. PHOTOGRAPHY — the only assets allowed in heroes, cards and galleries.
   ========================================================================== */

const PHOTO_PATHS = [
  '2025/11/The-Local-Food-Menu1.webp',
  '2025/11/The-Local-Food-Menu2.webp',
  '2025/11/The-Local-Food-Menu4.webp',
  '2025/11/The-Local-Food-Menu5.webp',
  '2025/11/The-Local-Food-Menu6.webp',
  '2025/11/The-Local-Food-Menu77.webp',
  '2025/11/The-Local-Food-Menu8.webp',
  '2025/11/The-Local-Food-Menu9.webp',
  '2025/11/The-Local-Food-Menu10.webp',
  '2025/11/Margharita-20K-scaled.webp',
  '2025/11/Spritz.20K-scaled.webp',
  '2025/11/The-Signatures-20K1-scaled.webp',
  '2025/06/TL-Smash-beef-burger-4.-38k-jpg-min-scaled.jpg',
  '2025/06/Lavender-Lemonades-min-scaled.jpg',
  '2025/08/2150878299.jpg',
];

/** Named photographs — use these, not raw strings. */
export const PHOTO = {
  plate1: m('2025/11/The-Local-Food-Menu1.webp'),
  plate2: m('2025/11/The-Local-Food-Menu2.webp'),
  plate4: m('2025/11/The-Local-Food-Menu4.webp'),
  plate5: m('2025/11/The-Local-Food-Menu5.webp'),
  plate6: m('2025/11/The-Local-Food-Menu6.webp'),
  plate7: m('2025/11/The-Local-Food-Menu77.webp'),
  plate8: m('2025/11/The-Local-Food-Menu8.webp'),
  plate9: m('2025/11/The-Local-Food-Menu9.webp'),
  plate10: m('2025/11/The-Local-Food-Menu10.webp'),
  margherita: m('2025/11/Margharita-20K-scaled.webp'),
  spritz: m('2025/11/Spritz.20K-scaled.webp'),
  signatures: m('2025/11/The-Signatures-20K1-scaled.webp'),
  burger: m('2025/06/TL-Smash-beef-burger-4.-38k-jpg-min-scaled.jpg'),
  lemonade: m('2025/06/Lavender-Lemonades-min-scaled.jpg'),
  tacos: m('2025/08/2150878299.jpg'),
};

/** Back-compat alias — HERO was the old name for the same idea. */
export const HERO = {
  primary: PHOTO.plate9,
  secondary: PHOTO.plate1,
  room: PHOTO.tacos,
  bar: PHOTO.spritz,
  table: PHOTO.plate8,
  evening: PHOTO.signatures,
  burger: PHOTO.burger,
  margherita: PHOTO.margherita,
  lemonade: PHOTO.lemonade,
};

export const PHOTO_GALLERY = [
  { src: PHOTO.plate1, alt: 'A plate served at The Local' },
  { src: PHOTO.signatures, alt: 'Signature cocktails at the bar' },
  { src: PHOTO.plate5, alt: 'Sharing plates on the table' },
  { src: PHOTO.burger, alt: 'The Local smash beef burger' },
  { src: PHOTO.plate9, alt: 'A main course, plated' },
  { src: PHOTO.spritz, alt: 'A spritz, poured at The Local' },
  { src: PHOTO.plate8, alt: 'From the kitchen at The Local' },
  { src: PHOTO.margherita, alt: 'Margherita pizza, fresh from the oven' },
  { src: PHOTO.plate2, alt: 'A dish on the pass' },
  { src: PHOTO.lemonade, alt: 'Lavender lemonade' },
  { src: PHOTO.plate10, alt: 'Long lunch at The Local' },
  { src: PHOTO.tacos, alt: 'Tacos at The Local' },
  { src: PHOTO.plate4, alt: 'A plate at The Local' },
  { src: PHOTO.plate6, alt: 'Served at the table' },
  { src: PHOTO.plate7, alt: 'On the pass' },
];

/** The mosaic on the home page — 8 cells, deliberately ordered. */
export const MOSAIC = [
  { src: PHOTO.burger, alt: 'The Local smash beef burger' },
  { src: PHOTO.signatures, alt: 'Signature cocktails at the bar' },
  { src: PHOTO.plate5, alt: 'Sharing plates on the table' },
  { src: PHOTO.margherita, alt: 'Margherita pizza, fresh from the oven' },
  { src: PHOTO.plate9, alt: 'A main course, plated' },
  { src: PHOTO.lemonade, alt: 'Lavender lemonade' },
  { src: PHOTO.plate1, alt: 'A plate served at The Local' },
  { src: PHOTO.spritz, alt: 'A spritz, poured at The Local' },
];

/* ==========================================================================
   2. MENU PAGES — scans of the printed menus. These ARE the menu; they are
   what wethelocal.co/menu displays. Never use them as decorative imagery.
   ========================================================================== */

const foodPage = (n) => `2025/11/The-Local-Food-Menu-${String(n).padStart(4, '0')}-scaled.webp`;
const drinkPage = (n) => `2025/11/The-Local-Drinks-Menu-${String(n).padStart(4, '0')}-scaled.webp`;

const FOOD_PAGE_PATHS = Array.from({ length: 11 }, (_, i) => foodPage(i + 1));
const DRINK_PAGE_PATHS = Array.from({ length: 15 }, (_, i) => drinkPage(i + 1));
const GOLDEN_HOUR_PATHS = ['2025/11/Golden-hour-Table-talker-02.webp'];

export const MENU_PAGES = {
  food: FOOD_PAGE_PATHS.map((p, i) => ({
    src: m(p),
    alt: `The Local food menu, page ${i + 1} of ${FOOD_PAGE_PATHS.length}`,
  })),
  drinks: DRINK_PAGE_PATHS.map((p, i) => ({
    src: m(p),
    alt: `The Local drinks menu, page ${i + 1} of ${DRINK_PAGE_PATHS.length}`,
  })),
  goldenHour: GOLDEN_HOUR_PATHS.map((p) => ({
    src: m(p),
    alt: 'The Local golden hour menu',
  })),
};

/* ==========================================================================
   2b. BRAND WEBFONT — "LORE", the hand-lettered face the WordPress site uses
   for its hero ("Every dish is a moment shared."). Self-hosted there under the
   CSS family name "Local". `npm run media` pulls these into public/fonts and
   the @font-face block in styles/global.css picks them up. Until then the
   display stack falls back to Fraunces, which is why nothing looks broken.
   ========================================================================== */

export const FONT_FILES = [
  '2025/11/LORE-Regular.woff2',
  '2025/11/LORE-Regular.woff',
  '2025/11/LORE-Regular.ttf',
  '2025/11/LORE-Bold.woff2',
  '2025/11/LORE-Bold.woff',
  '2025/11/LORE-Bold.ttf',
];

/* ==========================================================================
   3. COLLATERAL — designed print/social artwork. Looks like a photo in the
   media library, is not one. Cropping it destroys the lettering.
   ========================================================================== */

export const COLLATERAL = {
  goldenHourPoster: m('2025/11/Golden-Hour-0000.webp'),
  goldenHourTableTalker: m('2025/11/Golden-hour-Table-talker-02.webp'),
  bugolobiLettering: m('2025/11/Bugolobi-location-arrows-04.svg'),
  kyanjaLettering: m('2025/11/Kyanja-location-arrows-05.svg'),
};

export const TEXTURE = {
  pattern: m('2025/08/TL-pattern.svg'),
  motif: m('2025/11/motif-1.svg'),
  grain: m('2025/11/Grain-background.webp'),
  menuBg: m('2025/11/menu-bg-01.svg'),
};

/* ==========================================================================
   Everything `npm run media` should download.
   ========================================================================== */

export const MEDIA_PATHS = [
  ...PHOTO_PATHS,
  ...FOOD_PAGE_PATHS,
  ...DRINK_PAGE_PATHS,
  ...GOLDEN_HOUR_PATHS,
  '2025/11/Golden-Hour-0000.webp',
  '2025/08/TL-pattern.svg',
  '2025/11/motif-1.svg',
  '2025/11/Grain-background.webp',
  '2025/11/menu-bg-01.svg',
  '2025/11/Bugolobi-location-arrows-04.svg',
  '2025/11/Kyanja-location-arrows-05.svg',
  '2025/06/The-Local-Logos-01.svg',
  '2025/06/The-Local-Logos-02.svg',
  '2025/06/The-Local-Logos-03.svg',
  '2025/06/The-Local-Logos-04.svg',
];
